import { useState, useEffect, useCallback } from "react";
import styles from "./AdminBookings.module.css";
import { adminListCalBookings } from "../../../../lib/store";

const PER_PAGE = 10;

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-US", {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
};

export default function AdminBookings() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);

  const load = useCallback(() => {
    setLoading(true);
    setError("");
    adminListCalBookings()
      .then(setBookings)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = bookings.filter((b) => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return [b.title, b.name, b.email, b.status].some((v) =>
      (v || "").toLowerCase().includes(q),
    );
  });

  useEffect(() => {
    setPage(1);
  }, [query]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = filtered.slice(
    (currentPage - 1) * PER_PAGE,
    currentPage * PER_PAGE,
  );

  return (
    <div className={styles.wrap}>
      <div className={styles.headerRow}>
        <div>
          <div className={styles.pageTitle}>Bookings</div>
          <div className={styles.subtle}>
            {bookings.length} booking{bookings.length === 1 ? "" : "s"} from
            Cal.com
          </div>
        </div>
      </div>

      <input
        className={styles.search}
        placeholder="Search bookings..."
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />

      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={styles.idxCol}>#</th>
              <th>Event</th>
              <th>Name</th>
              <th>Email</th>
              <th>Date &amp; Time</th>
              <th>Status</th>
              <th>Guests</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td className={styles.empty} colSpan={7}>
                  <span className={styles.spinnerWrap}>
                    <span className={styles.spinner} />
                    <span>Loading bookings…</span>
                  </span>
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td className={styles.error} colSpan={7}>
                  {error}
                </td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td className={styles.empty} colSpan={7}>
                  No bookings found.
                </td>
              </tr>
            ) : (
              pageItems.map((b, i) => (
                <tr key={b.id}>
                  <td className={styles.idxCol}>
                    {(currentPage - 1) * PER_PAGE + i + 1}
                  </td>
                  <td className={styles.fileName}>{b.title || "—"}</td>
                  <td>{b.name || "—"}</td>
                  <td>{b.email || "—"}</td>
                  <td>{fmtDateTime(b.start)}</td>
                  <td>{b.status || "—"}</td>
                  <td>
                    {b.guests && b.guests.length ? b.guests.join(", ") : "—"}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {!loading && !error && totalPages > 1 && (
        <div className={styles.pagination}>
          <button
            className={styles.pageBtn}
            disabled={currentPage <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            ← Prev
          </button>
          <span className={styles.pageInfo}>
            Page {currentPage} of {totalPages}
          </span>
          <button
            className={styles.pageBtn}
            disabled={currentPage >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
}
